import { type FormEvent, useState } from "react";

import { ApiError } from "../api/client";
import { Alert, Button, Field, Input, PasswordInput } from "../components/ui";
import { useLogin, useSignup } from "../hooks/useAuth";

type Mode = "login" | "signup";

/**
 * Sign in, or create an account with an invite code.
 *
 * There is no open registration: the backend requires a valid invite, which
 * the owner mints with `python -m app.cli invite`.
 */
export default function Login() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  const login = useLogin();
  const signup = useSignup();
  const active = mode === "login" ? login : signup;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (mode === "login") {
      login.mutate({ email, password });
    } else {
      signup.mutate({
        email,
        password,
        display_name: displayName,
        invite_code: inviteCode,
      });
    }
  }

  const error = active.error;
  const message = error instanceof ApiError ? error.message : error ? "Something went wrong." : null;

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-12">
      <h1 className="text-2xl font-semibold text-accent-700">JobSearcher</h1>
      <p className="mt-1 text-sm text-gray-600">
        Tailored resumes and cover letters, generated with Claude.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        {message && <Alert kind="error">{message}</Alert>}

        {mode === "signup" && (
          <Field label="Your name">
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              required
            />
          </Field>
        )}

        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>

        <Field
          label="Password"
          hint={mode === "signup" ? "At least 12 characters." : undefined}
        >
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            minLength={mode === "signup" ? 12 : undefined}
            required
          />
        </Field>

        {mode === "signup" && (
          <Field label="Invite code" hint="Ask whoever runs this for one.">
            <Input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ABCD-EFGH-JKMN"
              required
            />
          </Field>
        )}

        <Button type="submit" disabled={active.isPending} className="w-full">
          {active.isPending
            ? "Working…"
            : mode === "login"
              ? "Sign in"
              : "Create account"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        {mode === "login" ? "Have an invite code?" : "Already have an account?"}{" "}
        <button
          type="button"
          onClick={() => setMode(mode === "login" ? "signup" : "login")}
          className="font-medium text-accent-600 hover:underline"
        >
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>
    </main>
  );
}
